/**
 * 온톨로지 슬라이스 로컬 draft 훅.
 *
 * - 초기값 (`initial`) 대비 "수정 여부(isDirty)" 와 개별 add/update/delete 를 제공
 * - 서버 반영은 호출자가 `useOntologyDraft().draft` 를 `updateOntologySlice(slug, ...)` 에 넘기는 방식
 * - 훅 내부는 **서버 호출을 전혀 하지 않는다** — UI 액션만 순수하게 관리
 *
 * 설계 노트:
 * - `shapes` 는 `(sourceKind, relation, targetKind)` 의 자연키로 식별 (equality 함수 `sameShape`)
 * - kind/domain/relation 이름을 바꾸면 `shapes` 는 참조를 잃으므로 호출자가 함께 cascade 해야 한다.
 *   이 훅은 rename cascade 를 수행하지 않는다 (Edit 화면에서 rename 을 금지하거나, 상위 핸들러에서 처리).
 */
import { useCallback, useMemo, useState } from "react"
import type {
	DomainDef,
	KindDef,
	OntologySlice,
	OntologyUsage,
	RelationDef,
	ShapeDef
} from "../../api/admin-client.ts"

/** 한 슬라이스의 초기값 스냅샷 ↔ 현재 draft. 변경 감지를 위한 JSON 직렬화 비교. */
function serialize(slice: OntologySlice): string {
	return JSON.stringify(slice)
}

function sameShape(a: ShapeDef, b: ShapeDef): boolean {
	return a.sourceKind === b.sourceKind && a.relation === b.relation && a.targetKind === b.targetKind
}

/**
 * `exactOptionalPropertyTypes: true` 와 "명시적 undefined 로 필드 제거" 를 양립시키기 위한 patch 타입.
 *
 * 각 키는 원본 타입이거나 `undefined` 일 수 있으며, `undefined` 가 들어오면 `applyPatch` 가 해당 키를 삭제한다.
 */
type PatchOf<T> = { [K in keyof T]?: T[K] | undefined }

/** undefined 값이 들어온 키는 결과 객체에서 제거되도록 하는 머지 헬퍼. */
function applyPatch<T extends object>(base: T, patch: PatchOf<T>): T {
	const next = { ...(base as Record<string, unknown>) }
	const patchRecord = patch as Record<string, unknown>
	for (const key of Object.keys(patchRecord)) {
		const value = patchRecord[key]
		if (value === undefined) {
			delete next[key]
		} else {
			next[key] = value
		}
	}
	return next as T
}

/**
 * `useOntologyDraft` 반환 객체.
 * 모든 mutator 는 내부 상태를 갱신하며, 서버 전송은 `draft` 를 외부 저장 핸들러에 넘기는 방식.
 */
export interface OntologyDraft {
	/** 현재 draft 슬라이스 — Save 시 호출자가 그대로 서버에 PATCH 하면 된다 */
	readonly draft: OntologySlice
	/** 원본 (initial) 대비 변경 여부 */
	readonly isDirty: boolean
	/** 마지막 초기화 이후 원본 슬라이스 (reset 기준점) */
	readonly baseline: OntologySlice

	/** 전체 교체 (preset 적용/Import 등) */
	reset(next: OntologySlice, becomeBaseline?: boolean): void
	/** baseline 으로 롤백 */
	discard(): void
	/** 현재 draft 를 새 baseline 으로 고정 — Save 성공 후 호출 */
	commit(): void

	addKind(kind: KindDef): void
	updateKind(name: string, patch: PatchOf<KindDef>): void
	removeKind(name: string): void

	addDomain(domain: DomainDef): void
	updateDomain(name: string, patch: PatchOf<DomainDef>): void
	removeDomain(name: string): void

	addRelation(relation: RelationDef): void
	updateRelation(name: string, patch: PatchOf<RelationDef>): void
	removeRelation(name: string): void

	addShape(shape: ShapeDef): void
	updateShape(key: ShapeDef, patch: PatchOf<ShapeDef>): void
	removeShape(key: ShapeDef): void

	/**
	 * 사용량 (usage) 에 있으나 draft 에 없는 이름들을 일괄 흡수.
	 * 각 항목은 기본 displayName(=name) 만 채워서 추가된다 — 이후 MetaEditPanel 에서 메타를 보강.
	 */
	absorbOrphans(usage: OntologyUsage): void
}

function toKindMap(kinds: ReadonlyArray<KindDef>): ReadonlyArray<KindDef> {
	return kinds
}

/**
 * 온톨로지 draft 훅.
 *
 * @param initial 초기 슬라이스 — 서버에서 내려온 값. 화면 최초 진입 시 한 번 고정된다.
 * @example
 *   const draft = useOntologyDraft(service.ontologySlice)
 *   <button disabled={!draft.isDirty} onClick={() => save(draft.draft)}>Save</button>
 */
export function useOntologyDraft(initial: OntologySlice): OntologyDraft {
	const [baseline, setBaseline] = useState<OntologySlice>(initial)
	const [draft, setDraft] = useState<OntologySlice>(initial)

	const isDirty = useMemo(() => serialize(draft) !== serialize(baseline), [draft, baseline])

	const reset = useCallback((next: OntologySlice, becomeBaseline = false) => {
		setDraft(next)
		if (becomeBaseline) setBaseline(next)
	}, [])

	const discard = useCallback(() => {
		setDraft(baseline)
	}, [baseline])

	const commit = useCallback(() => {
		setBaseline(draft)
	}, [draft])

	const addKind = useCallback((kind: KindDef) => {
		setDraft((prev) => {
			if (prev.kinds.some((k) => k.name === kind.name)) return prev
			return { ...prev, kinds: [...toKindMap(prev.kinds), kind] }
		})
	}, [])

	const updateKind = useCallback((name: string, patch: PatchOf<KindDef>) => {
		setDraft((prev) => ({
			...prev,
			kinds: prev.kinds.map((k) => (k.name === name ? applyPatch(k, patch) : k))
		}))
	}, [])

	const removeKind = useCallback((name: string) => {
		setDraft((prev) => ({
			...prev,
			kinds: prev.kinds.filter((k) => k.name !== name),
			// kind 삭제 시 해당 kind 를 참조하는 shape 도 함께 제거
			shapes: prev.shapes.filter((s) => s.sourceKind !== name && s.targetKind !== name)
		}))
	}, [])

	const addDomain = useCallback((domain: DomainDef) => {
		setDraft((prev) => {
			if (prev.domains.some((d) => d.name === domain.name)) return prev
			return { ...prev, domains: [...prev.domains, domain] }
		})
	}, [])

	const updateDomain = useCallback((name: string, patch: PatchOf<DomainDef>) => {
		setDraft((prev) => ({
			...prev,
			domains: prev.domains.map((d) => (d.name === name ? applyPatch(d, patch) : d))
		}))
	}, [])

	const removeDomain = useCallback((name: string) => {
		setDraft((prev) => ({ ...prev, domains: prev.domains.filter((d) => d.name !== name) }))
	}, [])

	const addRelation = useCallback((relation: RelationDef) => {
		setDraft((prev) => {
			if (prev.relations.some((r) => r.name === relation.name)) return prev
			return { ...prev, relations: [...prev.relations, relation] }
		})
	}, [])

	const updateRelation = useCallback((name: string, patch: PatchOf<RelationDef>) => {
		setDraft((prev) => ({
			...prev,
			relations: prev.relations.map((r) => (r.name === name ? applyPatch(r, patch) : r))
		}))
	}, [])

	const removeRelation = useCallback((name: string) => {
		setDraft((prev) => ({
			...prev,
			relations: prev.relations.filter((r) => r.name !== name),
			// relation 삭제 시 해당 relation 을 참조하는 shape 도 함께 제거
			shapes: prev.shapes.filter((s) => s.relation !== name)
		}))
	}, [])

	const addShape = useCallback((shape: ShapeDef) => {
		setDraft((prev) => {
			if (prev.shapes.some((s) => sameShape(s, shape))) return prev
			return { ...prev, shapes: [...prev.shapes, shape] }
		})
	}, [])

	const updateShape = useCallback((key: ShapeDef, patch: PatchOf<ShapeDef>) => {
		setDraft((prev) => ({
			...prev,
			shapes: prev.shapes.map((s) => (sameShape(s, key) ? applyPatch(s, patch) : s))
		}))
	}, [])

	const removeShape = useCallback((key: ShapeDef) => {
		setDraft((prev) => ({ ...prev, shapes: prev.shapes.filter((s) => !sameShape(s, key)) }))
	}, [])

	const absorbOrphans = useCallback((usage: OntologyUsage) => {
		setDraft((prev) => {
			const kindSet = new Set(prev.kinds.map((k) => k.name))
			const domainSet = new Set(prev.domains.map((d) => d.name))
			const relationSet = new Set(prev.relations.map((r) => r.name))

			const newKinds = Object.keys(usage.kinds)
				.filter((name) => !kindSet.has(name))
				.map<KindDef>((name) => ({ name, displayName: name }))
			const newDomains = Object.keys(usage.domains)
				.filter((name) => !domainSet.has(name))
				.map<DomainDef>((name) => ({ name, displayName: name }))
			const newRelations = Object.keys(usage.relations)
				.filter((name) => !relationSet.has(name))
				.map<RelationDef>((name) => ({ name, displayName: name }))

			if (newKinds.length === 0 && newDomains.length === 0 && newRelations.length === 0) return prev

			return {
				...prev,
				kinds: [...prev.kinds, ...newKinds],
				domains: [...prev.domains, ...newDomains],
				relations: [...prev.relations, ...newRelations]
			}
		})
	}, [])

	return {
		draft,
		isDirty,
		baseline,
		reset,
		discard,
		commit,
		addKind,
		updateKind,
		removeKind,
		addDomain,
		updateDomain,
		removeDomain,
		addRelation,
		updateRelation,
		removeRelation,
		addShape,
		updateShape,
		removeShape,
		absorbOrphans
	}
}
